import { BulkOperationProps } from '@type/bulk-operations';
import { useBjForm } from '@hooks/useBjForm';
import { useTranslation } from 'next-i18next';
import { useSwrSendGroupMessage } from '@hooks/useSwrApi/endpoints/bulk-operations';
import { Button, Textarea } from '@components/common/Forms';
import { Modal } from '@components/common';
import { ChatBubbleLeftRightIcon } from '@heroicons/react/20/solid';
import { i18nConfig } from 'i18n-config';
import { useOpenBulkModal } from './hooks/useOpenBulkModal';

interface BulkMessageProps extends BulkOperationProps {
  hideTriggerIcon?: boolean;
  triggerDisabled?: boolean;
}

/**
 * @description Send bulk message component for the job applicants list.
 *
 * - **getSelectedApplicants**: Get the selected applicants. Function that returns an array of selected applicants.
 * - **selectAll**: Boolean value that indicates if all applicants are selected.
 * - **status**: Status of the applicants.
 * - **jobSlug**: The job slug.
 * - **locale**: The locale.
 * - **queryParams**: The filters query params object.
 */
export const BulkMessage = (props: BulkMessageProps) => {
  const {
    getSelectedApplicants,
    jobSlug = '',
    locale = i18nConfig.defaultLocale,
    selectAll,
    status,
    selectByColumn,
    queryParams,
    normalizeSelectedApplicantsForBulkAction,
    hideTriggerIcon = false,
    triggerDisabled = false,
  } = props;

  // Translation
  const { t } = useTranslation('common');

  // Handle the bulk applicants modal toggle and base post object build
  const {
    isOpen,
    toggleBulkModal,
    closeOnSuccess,
    basePostObject,
  } = useOpenBulkModal({
    getSelectedApplicants, selectAll, status, queryParams, selectByColumn, normalizeSelectedApplicantsForBulkAction,
  });

  // BJ Form
  const {
    handleCallbackSubmit,
    record,
  } = useBjForm({
    wizardData: basePostObject || {},
  });

  // SWR: Send group message
  const {
    trigger,
    isMutating,
  } = useSwrSendGroupMessage(jobSlug, locale, closeOnSuccess);


  // Render component
  // *********************************
  return (
    <>
      {/* TRIGGER BUTTON */}
      <Button
        styling="text"
        size="sm"
        color="ink"
        className="grow px-4"
        disabled={triggerDisabled}
        onClick={toggleBulkModal}
        aria-label={t('cv.action.message')}
      >
        {!hideTriggerIcon && <ChatBubbleLeftRightIcon className="mr-2 size-5 sm:mr-1" />}
        <span>{t('cv.action.message')}</span>
      </Button>


      {/* BULK MESSAGES MODAL */}
      <Modal isOpen={isOpen} onClose={toggleBulkModal}>
        <form noValidate onSubmit={(event) => handleCallbackSubmit(event, trigger)}>

          {/* Modal header */}
          <Modal.Header onClose={toggleBulkModal}>
            {t('cv.action.message')}
          </Modal.Header>

          {/* Modal body */}
          <Modal.Body className="bg-surface-100">
            <p>{t('cv.action.groupmessage.desc')}</p>
            <Textarea
              placeholder={t('messages.conversation.new_message')}
              className="asOptional mb-1 mt-6"
              rows={7}
              minLength={3}
              required
              {...record('messageBody')}
            />
          </Modal.Body>

          {/* Modal footer */}
          <Modal.Footer className="text-right">
            <Button
              type="submit"
              color="secondary"
              rounding="full"
              size="sm"
              className="!px-7"
              isLoading={isMutating}
            >
              <ChatBubbleLeftRightIcon className="size-5 sm:mr-1" />
              {t('global.action.send')}
            </Button>
          </Modal.Footer>

        </form>
      </Modal>
    </>
  );
};
